import { supabase } from "@/integrations/supabase/client";
import { SUPABASE_URL, SUPABASE_KEY } from "@/lib/supabase";

export type SignupResult =
  | { ok: true; status: "created" | "duplicate" }
  | { ok: false; error: string };

async function directRestInsert(email: string, meta?: Record<string, any>) {
  const res = await fetch(`${SUPABASE_URL}/rest/v1/waitlist`, {
    method: "POST",
    headers: {
      apikey: SUPABASE_KEY,
      Authorization: `Bearer ${SUPABASE_KEY}`,
      "Content-Type": "application/json",
      Prefer: "return=minimal",
    },
    body: JSON.stringify({
      email,
      source: meta?.source ?? "website",
      metadata: meta ?? {},
    }),
  });

  if (res.status === 409 || res.status === 202 || res.status === 204) {
    // 409 (conflict) or async/minimal response – treat as duplicate/success
    return { ok: true as const, status: "duplicate" as const };
  }

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`HTTP ${res.status}: ${text || res.statusText}`);
  }

  return { ok: true as const, status: "created" as const };
}

export function useWaitlist() {
  const signup = async (
    email: string,
    meta?: Record<string, any>
  ): Promise<SignupResult> => {
    if (!SUPABASE_URL || !SUPABASE_KEY) {
      return {
        ok: false,
        error:
          "Missing Supabase configuration. Set VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY in Project → Settings → Environment Variables, then publish.",
      };
    }

    // 1) Try supabase-js if configured
    if (supabase) {
      try {
        const { error } = await supabase.from("waitlist").insert({
          email,
          source: meta?.source ?? "website",
          metadata: meta ?? {},
        });

        if (!error) return { ok: true, status: "created" };

        // Unique violation code -> duplicate
        if ((error as any).code === "23505") return { ok: true, status: "duplicate" };

        // If fetch failed under the hood, fall through to direct REST
        if (/Failed to fetch/i.test(error.message)) {
          console.warn("[SB] supabase-js failed to fetch; trying REST fallback…");
        } else {
          console.error("Waitlist insert error:", error);
          return { ok: false, error: error.message || "insert_failed" };
        }
      } catch (e: any) {
        if (!/Failed to fetch/i.test(e?.message ?? "")) {
          console.error("Waitlist unexpected error:", e);
          return { ok: false, error: e?.message ?? "unknown" };
        }
        console.warn("[SB] supabase-js threw 'Failed to fetch'; trying REST fallback…");
      }
    }

    // 2) Fallback: direct REST call (works even if client init fails)
    try {
      return await directRestInsert(email, meta);
    } catch (e: any) {
      console.error("Waitlist REST insert error:", e);
      return { ok: false, error: e?.message ?? "rest_insert_failed" };
    }
  };

  return { signup };
}